import { ContextsLayout } from "@/components/layouts/contexts-layout";
import { Button } from "@/components/ui/button";
import { Search } from "@/components/ui/search";
import { PlusIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { useEffect, useState } from "react";
import { useGetAllContextPacks } from "@/api/studio/contexts/queries";
import { CardBadge, CardBody, CardSkeleton } from "@/components/ui/card";
import { navigate } from "wouter/use-browser-location";
import { ContextPack } from "@/api/studio/contexts/types/context.type";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { ContextsEmptyState } from "@/features/contexts/components/contexts-empty-state";
import ENV from "@/utils/env";
import { GetAllContextPacksReq } from "@/api/studio/contexts/api";
import { SelectSort } from "@/components/ui/select";
import { CreateContextLibraryPackModal } from "@/features/contexts/modals/create-context-library-pack-modal";
import { useModalStore } from "@/stores/use-modal-store";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { DragonflyIcon } from "@/assets/icons";
import { UseCaseDropdown } from "@/features/contexts/components/use-case-dropdown";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { CreatedByDropdown } from "@/features/contexts/components/created-by-dropdown";
import { Error } from "@/components/fallbacks/error-boundaries/error";

/*
 * ContextsRoute component
 * Page to view all contexts
 */
export const ContextsRoute = () => {
  /* State */
  const [view, setView] = useState<GetAllContextPacksReq["view"]>("all");
  const [query, setQuery] = useState("");
  const [orderBy, setOrderBy] = useState<GetAllContextPacksReq["orderBy"]>("updatedAt");
  const [sortBy, setSortBy] = useState<GetAllContextPacksReq["sortBy"]>("desc");
  const [pageIndex, setPageIndex] = useState(1);
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isDragonfly, setIsDragonfly] = useState<boolean>(false);

  /* Hooks */
  const { segmentTrackEvent, segmentPageEvent } = useSegment();

  /* Stores */
  const { open } = useModalStore((state) => state.modalActions);
  const theme = useGlobalStore((state) => state.theme);

  /* Queries */
  const {
    data: contexts,
    isLoading: contextsLoading,
    isError: isContextsError,
  } = useGetAllContextPacks({
    view,
    query,
    sortBy,
    pageIndex,
    orderBy,
    itemsPerPage: 8,
    useCaseIds: selectedUseCases,
    userIds: selectedUsers,
    ...(isDragonfly !== null && { isDragonfly }),
  });

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the report in depth page
    if (theme)
      segmentPageEvent("Contexts", {
        title: "Contexts | Dragonfly AI Studio",
        theme: theme,
      });
  }, []);

  /* Handlers */
  const handleOpen = (context: ContextPack) => {
    // if the context is not published and has no contexts, navigate to the context select page
    if (!context.isPublished && context.contextCount === 0) {
      // Segment tracking
      segmentTrackEvent("Context Pack Creation Resumed", {
        contextPackId: context.id,
        contextPackName: context.name,
        useCaseId: context.useCase.id,
        useCaseName: context.useCase.name,
        contextCount: context.contextCount,
        route: "Context Upload",
      });
      navigate(`/contexts/${context.id}/context-select`);
    }
    // if the context is not published and has contexts, navigate to the context dropzone page
    else if (!context.isPublished && context.contextCount > 0) {
      // Segment tracking
      segmentTrackEvent("Context Pack Creation Resumed", {
        contextPackId: context.id,
        contextPackName: context.name,
        useCaseId: context.useCase.id,
        useCaseName: context.useCase.name,
        contextCount: context.contextCount,
        route: "Context Dropzone",
      });
      navigate(`/contexts/${context.id}/context-dropzone`);
    }
    // otherwise, navigate to the context page
    else {
      navigate(`/contexts/${context.id}`);
    }
  };

  const handleCreateContextPack = () => {
    open(<CreateContextLibraryPackModal />);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Reset to first page on search
    setPageIndex(1);
    setQuery(e.target.value);
  };

  /* Render */
  return (
    <ContextsLayout>
      {/* Primary */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Contexts</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" onClick={handleCreateContextPack}>
            <PlusIcon className="size-5" /> New context pack
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary */}
      <Toolbar>
        <ToolbarItem>
          <Search onChange={handleSearch} />

          <CustomDropdown
            triggerBtn={
              <Button>
                {view && view.charAt(0).toUpperCase() + view.slice(1)}
                <ChevronDownIcon className="size-4" />
              </Button>
            }
            triggerBtnClassname="p-0"
          >
            <CustomDropdownItem onClick={() => setView("all")}>All</CustomDropdownItem>
            <CustomDropdownItem onClick={() => setView("draft")}>Draft</CustomDropdownItem>
            <CustomDropdownItem onClick={() => setView("published")}>Published</CustomDropdownItem>
            <CustomDropdownItem onClick={() => setView("archived")}>Archived</CustomDropdownItem>
          </CustomDropdown>

          <UseCaseDropdown useCases={contexts?.useCases || []} onSelectionChange={(selectedIds) => setSelectedUseCases(selectedIds)} />
          <CreatedByDropdown
            teamMembers={contexts?.users || []}
            onSelectionChange={(userId) => setSelectedUsers(userId)}
            isDragonfly={isDragonfly}
            setIsDragonfly={setIsDragonfly}
          />
        </ToolbarItem>
      </Toolbar>

      {/* Tertiary */}
      <Toolbar>
        <ToolbarItem>
          {!isContextsError && (
            <h2>
              {contexts?.totalItems} context {contexts?.totalItems === 1 ? "pack" : "packs"}
            </h2>
          )}
        </ToolbarItem>
        <ToolbarItem>
          <SelectSort setOrderBy={setOrderBy} setSortBy={setSortBy} />
        </ToolbarItem>
      </Toolbar>

      <div className="flex flex-col gap-7 pb-20">
        <div className="flex gap-4 flex-wrap w-full">
          {/* Skeleton loader */}
          {contextsLoading && Array.from({ length: 5 }).map((_, index) => <CardSkeleton key={index} />)}

          {/* Error fallback */}
          {isContextsError && <Error />}

          {/* Empty fallback */}
          {contexts?.packs.length === 0 && (
            <ContextsEmptyState
              onClick={handleCreateContextPack}
              emptyMessage={
                <p className="text-base-content/70">
                  Create a new context pack or{" "}
                  <a
                    href="https://academy.dragonflyai.co/en/articles/11059635-context-3-0"
                    className="underline"
                    rel="noreferrer"
                    target="_blank"
                    style={{ textUnderlineOffset: "2px" }}
                  >
                    learn
                  </a>{" "}
                  how context work
                </p>
              }
              className="mx-auto mt-auto"
            />
          )}

          {/* Context packs */}
          {contexts?.packs?.map((context) => (
            <Card
              key={context.id}
              onClick={() => handleOpen(context)}
              imageUrl={context.firstContextId ? `${ENV.API_PLATFORM_URL}/v3/contexts/${context.firstContextId}/image?maxWidth=400&maxHeight=400` : undefined}
            >
              {!context.isPublished && <CardBadge>Draft</CardBadge>}
              <CardBody className="relative gap-1.5" title={context.name}>
                {!context.isDragonfly && <p className="text-base-content/70">{context.username}</p>}
                {context.isDragonfly && <p className="text-base-content/70">Dragonfly</p>}
                <div className="flex align-center">
                  <Badge className="bg-base-200 px-2.5 mr-2.5">{context.useCase.name}</Badge>
                  <Badge className="bg-base-200 px-2.5">
                    <Square2StackIcon className="size-3.5 mr-2" />
                    {context.contextCount}
                  </Badge>
                </div>
              </CardBody>
              {context.isDragonfly && (
                <CardBadge className="p-1 rounded-lg w-auto h-auto">
                  <DragonflyIcon className="size-4" />
                </CardBadge>
              )}
            </Card>
          ))}
        </div>

        {/* Pagination */}
        <Pagination currentPage={pageIndex} totalPages={contexts?.maxPageIndex ?? 1} onPageChange={setPageIndex} />
      </div>
    </ContextsLayout>
  );
};
